import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useSearchParams, useLocation, Link } from 'react-router-dom';
import { isAndroid } from './ar/androidSupport';
import { arEntry, scannedRoomUrl, supportsRoomAR } from './ar/nativeSupport';
import { AuthProvider, hasRole, useAuth } from './auth/AuthContext';
import { Home } from './pages/Home';
import { Login } from './pages/Login';
import type { UserRole } from './types';

const ARView = lazy(() => import('./pages/ARView').then((module) => ({ default: module.ARView })));
const ScanAR = lazy(() => import('./pages/ScanAR').then((module) => ({ default: module.ScanAR })));
const RoomAR = lazy(() => import('./pages/RoomAR').then((module) => ({ default: module.RoomAR })));
const AndroidAR = lazy(() => import('./pages/AndroidAR').then((module) => ({ default: module.AndroidAR })));
const Library = lazy(() => import('./pages/Library').then((module) => ({ default: module.Library })));
const AdminDashboard = lazy(() => import('./pages/Admin').then((module) => ({ default: module.AdminDashboard })));
const ObjectForm = lazy(() => import('./pages/Admin').then((module) => ({ default: module.ObjectForm })));
const Users = lazy(() => import('./pages/Users').then((module) => ({ default: module.Users })));
const Settings = lazy(() => import('./pages/Settings').then((module) => ({ default: module.Settings })));

export default function App() { return <AuthProvider><BrowserRouter><AuthenticatedApp/></BrowserRouter></AuthProvider>; }
function AuthenticatedApp() {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <div className="splash">OpenAR beveiligen…</div>;
  if (!user && !['/ar'].includes(pathname) && !pathname.startsWith('/room/') && !pathname.startsWith('/o/')) {
    if (pathname !== '/') return <Login/>;
    return <main className="page narrow"><h1>OpenAR</h1><p>Scan een OpenAR QR-code om een openbaar model te bekijken. Je hebt geen account nodig.</p><Link className="primary large" to="/ar">QR-code scannen</Link><p><Link to="/login">Inloggen voor beheer</Link></p></main>;
  }
  return <Suspense fallback={<div className="splash">OpenAR laden…</div>}><Routes>
    <Route path="/" element={<Home/>}/><Route path="/ar" element={<ArEntry/>}/><Route path="/room/:publicId" element={<NativeAR/>}/><Route path="/o/:publicId" element={<NavigateToAr/>}/><Route path="/library" element={<Library/>}/>
    <Route path="/admin" element={<RoleGate roles={['admin','editor']}><AdminDashboard/></RoleGate>}/>
    <Route path="/admin/new" element={<RoleGate roles={['admin','editor']}><ObjectForm/></RoleGate>}/>
    <Route path="/admin/:id" element={<RoleGate roles={['admin','editor']}><ObjectForm/></RoleGate>}/>
    <Route path="/users" element={<RoleGate roles={['admin']}><Users/></RoleGate>}/>
    <Route path="/settings" element={<Settings/>}/><Route path="*" element={<Home/>}/>
  </Routes></Suspense>;
}
function NativeAR() { return isAndroid() ? <AndroidAR/> : <RoomAR/>; }
function ArEntry() {
  const [params] = useSearchParams();
  const entry = arEntry(params, isAndroid() || supportsRoomAR());
  if (entry === 'room') return <Navigate replace to={scannedRoomUrl(params.get('object')!)}/>;
  return entry === 'scan' ? <ScanAR/> : <ARView/>;
}
function RoleGate({ roles, children }: { roles: UserRole[]; children: ReactNode }) { const { user } = useAuth(); return hasRole(user, roles) ? <>{children}</> : <Home/>; }
function NavigateToAr() { const id = location.pathname.split('/').pop(); location.replace('/ar?object=' + encodeURIComponent(id ?? 'demo')); return null; }
